import React, { useState, useEffect } from 'react';
import { buildApiUrl } from '../config';
import './DailySales.css';

const DailySales = () => {
    const [dailySales, setDailySales] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [userRole, setUserRole] = useState('');
    const [editingRecord, setEditingRecord] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editFormData, setEditFormData] = useState({});

    useEffect(() => {
        // Get user role from session storage
        const userData = JSON.parse(sessionStorage.getItem('user'));
        console.log('User data from session storage:', userData);
        if (userData && userData.role) {
            setUserRole(userData.role);
            console.log('User role set to:', userData.role);
        } else {
            console.log('No user role found in session storage');
        }
    }, []);

    useEffect(() => {
        if (selectedDate) {
            fetchDailySales();
        }
    }, [selectedDate]);

    const fetchDailySales = async () => {
        try {
            const response = await fetch(buildApiUrl(`api/dailySales?date=${selectedDate}`));
            const data = await response.json();
            if (data.success) {
                console.log('Daily Sales Data:', data.data); // Debug log
                setDailySales(data.data);
            } else {
                console.error('Error fetching daily sales data:', data.error);
            }
        } catch (err) {
            console.error('Error fetching daily sales data:', err);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString.toLowerCase() === 'null') return '-'; // Handle missing dates

        let parsedDate;

        // Try parsing YYYY-MM-DD format
        if (/\d{4}-\d{2}-\d{2}/.test(dateString)) {
            parsedDate = new Date(dateString);
        }
        // Try parsing DD-MM-YYYY format
        else if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('-');
            parsedDate = new Date(`${year}-${month}-${day}`);
        }
        // Try parsing DD/MM/YYYY format
        else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
            const [day, month, year] = dateString.split('/');
            parsedDate = new Date(`${year}-${month}-${day}`);
        }
        else {
            parsedDate = new Date(dateString);
        }

        // Final check: If parsedDate is invalid, return fallback
        if (isNaN(parsedDate.getTime())) return 'Invalid Date';

        return parsedDate.toLocaleDateString('en-GB'); // Returns format: DD-MM-YYYY
    };


    const totals = dailySales.reduce(
        (acc, sale) => {
            acc.OilChange += parseFloat(sale.OilChange) || 0;
            acc.Washing += parseFloat(sale.Washing) || 0;
            acc.Recovery += parseFloat(sale.Recovery) || 0;
            acc.CreditSales += parseFloat(sale.CreditSales) || 0;
            acc.Commission -= parseFloat(sale.Commission) || 0;
            // Include regular expenses and settlements, but exclude overtime payments from expense calculation
            // Overtime payments are identified by having OvertimeID AND Service starting with "Overtime:"
            if (sale.Expense && !(sale.OvertimeID && sale.Service?.startsWith('Overtime:'))) {
                acc.Expense += parseFloat(sale.Expense) || 0;
            }
            acc.Balance = sale.Balance || acc.Balance;
            return acc;
        },
        { OilChange: 0, Washing: 0, Recovery: 0, CreditSales: 0, Commission: 0, Expense: 0, Balance: 0 }
    );

    // Calculate cash received correctly: Cash Sales + Recovery
    // Cash Sales = Total Sales (OilChange + Washing) - Credit Sales + Recovery
    totals.cashReceived = (totals.OilChange + totals.Washing - totals.CreditSales) + totals.Recovery;


    // Export currently displayed Daily Sales to CSV
    const exportDailySalesCSV = () => {
        if (!dailySales || dailySales.length === 0) {
            alert('No data to export');
            return;
        }

        const headers = [
            'Sr',
            'Date',
            'Service',
            'Vehicle Number',
            'Vehicle Type',
            'Driver',
            'Company Name',
            'Oil Change',
            'Washing',
            'Recovery',
            'Credit Sales',
            'Commission',
            'Expense',
            'Balance',
            'SaleID',
            'ExpenseID',
            'PaymentID',
            'OvertimeID',
            'OvertimeAmount',
            'SettlementID',
            'Remarks'
        ];

        const rows = dailySales.map((sale, idx) => [
            String(idx + 1),
            sale.Date ? formatDate(sale.Date) : '-',
            sale.Service ?? '-',
            sale.VehicleNumber ?? '-',
            sale.VehicleType ?? '-',
            sale.Driver ?? '-',
            sale.CompanyName ?? '-',
            sale.OilChange ?? '-',
            sale.Washing ?? '-',
            sale.Recovery ?? '-',
            sale.CreditSales ?? '-',
            sale.Commission ?? '-',
            sale.Expense ?? '-',
            sale.Balance ?? '-',
            sale.SaleID ?? '-',
            sale.ExpenseID ?? '-',
            sale.PaymentID ?? '-',
            sale.OvertimeID ?? '-',
            sale.OvertimeAmount ?? '-',
            sale.SettlementID ?? '-',
            sale.Remarks ?? '-',
        ]);

        // Add totals row at the end
        rows.push([
            'Total', '', '', '', '', '', '',
            totals.OilChange.toFixed(2),
            totals.Washing.toFixed(2),
            totals.Recovery.toFixed(2),
            totals.CreditSales.toFixed(2),
            Math.abs(totals.Commission).toFixed(2),
            totals.Expense.toFixed(2),
            String(totals.Balance),
            '', '', '', '', '', '', ''
        ]);

        const csv = [headers, ...rows]
            .map((row) => row
                .map((cell) => {
                    const val = cell === null || cell === undefined ? '' : String(cell);
                    // Escape quotes and wrap in quotes if contains comma or newline
                    const escaped = val.replace(/"/g, '""');
                    return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
                })
                .join(','))
            .join('\n');

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileDate = selectedDate || new Date().toISOString().split('T')[0];
        link.download = `Daily_Sales_${fileDate}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleEdit = (sale) => {
        setEditingRecord(sale);
        setEditFormData({
            // Common fields
            servicePrice: sale.OilChange || sale.Washing || sale.CreditSales || sale.Expense || sale.Recovery,
            notes: sale.Remarks,
            // Sales specific
            paymentMethod: sale.payment_method || (sale.CreditSales ? 'credit' : 'cash'), // Set default based on CreditSales
            commission: sale.Commission,
            // Expense specific
            expenseAmount: sale.Expense,
            expenseType: sale.Service?.replace('Expense: ', ''),
            expenseDescription: sale.Remarks,
            // Payment specific
            paymentAmount: sale.Recovery,
            paymentNotes: sale.Remarks
        });
        setShowEditModal(true);
    };

    const handleEditSubmit = async () => {
        if (!window.confirm('Are you sure you want to update this record?')) {
            return;
        }

        try {
            const response = await fetch(buildApiUrl('api/dailySales/edit'), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    saleId: editingRecord.SaleID,
                    expenseId: editingRecord.ExpenseID,
                    paymentId: editingRecord.PaymentID,
                    ...editFormData
                })
            });

            const data = await response.json();

            if (data.success) {
                // Refresh the data
                fetchDailySales();
                setShowEditModal(false);
                alert('Record updated successfully');
            } else {
                alert('Failed to update record: ' + data.error);
            }
        } catch (error) {
            console.error('Error updating record:', error);
            alert('Error updating record. Please try again.');
        }
    };

    const renderEditForm = () => {
        if (!editingRecord) return null;

        return (
            <div className="edit-modal">
                <div className="edit-modal-content">
                    <h2>Edit Record</h2>
                    {editingRecord.SaleID && (
                        <>
                            <div className="form-group">
                                <label>Service Price:</label>
                                <input
                                    type="number"
                                    value={editFormData.servicePrice || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, servicePrice: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Payment Method:</label>
                                <input
                                    type="text"
                                    value={editFormData.paymentMethod || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, paymentMethod: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Commission:</label>
                                <input
                                    type="number"
                                    value={editFormData.commission || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, commission: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    {editingRecord.ExpenseID && (
                        <>
                            <div className="form-group">
                                <label>Expense Amount:</label>
                                <input
                                    type="number"
                                    value={editFormData.expenseAmount || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, expenseAmount: e.target.value })}
                                />
                            </div>
                            <div className="form-group">
                                <label>Expense Type:</label>
                                <input
                                    type="text"
                                    value={editFormData.expenseType || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, expenseType: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    {editingRecord.PaymentID && (
                        <>
                            <div className="form-group">
                                <label>Payment Amount:</label>
                                <input
                                    type="number"
                                    value={editFormData.paymentAmount || ''}
                                    onChange={(e) => setEditFormData({ ...editFormData, paymentAmount: e.target.value })}
                                />
                            </div>
                        </>
                    )}
                    <div className="form-group">
                        <label>Notes:</label>
                        <input
                            type="text"
                            value={editFormData.notes || ''}
                            onChange={(e) => setEditFormData({ ...editFormData, notes: e.target.value })}
                        />
                    </div>
                    <div className="modal-buttons">
                        <button onClick={handleEditSubmit}>Save Changes</button>
                        <button onClick={() => setShowEditModal(false)}>Cancel</button>
                    </div>
                </div>
            </div>
        );
    };

    const handleDelete = async (sale) => {
        // Show confirmation dialog
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        try {
            const response = await fetch(buildApiUrl('api/dailySales/delete'), {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    saleId: sale.SaleID,
                    expenseId: sale.ExpenseID,
                    paymentId: sale.PaymentID,
                    overtimeId: sale.OvertimeID,
                    settlementId: sale.SettlementID
                })
            });

            const data = await response.json();

            if (data.success) {
                // Remove the deleted record from the state
                setDailySales(prevSales => prevSales.filter(item => {
                    if (sale.SaleID) return item.SaleID !== sale.SaleID;
                    if (sale.ExpenseID) return item.ExpenseID !== sale.ExpenseID;
                    if (sale.PaymentID) return item.PaymentID !== sale.PaymentID;
                    if (sale.OvertimeID) return item.OvertimeID !== sale.OvertimeID;
                    if (sale.SettlementID) return item.SettlementID !== sale.SettlementID;
                    return true;
                }));

                alert('Record deleted successfully');
            } else {
                alert('Failed to delete record: ' + data.error);
            }
        } catch (error) {
            console.error('Error deleting record:', error);
            alert('Error deleting record. Please try again.');
        }
    };

    return (
        <div className="daily-sales-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'space-between' }}>
                <h1 style={{ margin: 0 }}>Daily Sales</h1>
                <button
                    onClick={exportDailySalesCSV}
                    style={{
                        background: 'none',
                        border: '1px solid #ccc',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#217346',
                        padding: '6px 10px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                    title="Export current table to CSV"
                >
                    📊
                </button>
            </div>
            <div className="filter-container">
                <label htmlFor="date-filter">Select Date:</label>
                <input
                    type="date"
                    id="date-filter"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                />
            </div>
            <div className="table-container">
                <table className="daily-sales-table">
                    <thead>
                        <tr>
                            <th>Sr</th>
                            <th>Date</th>
                            <th>Service</th>
                            <th>Vehicle Number</th>
                            <th>Vehicle Type</th>
                            <th>Driver</th>
                            <th>Company Name</th>
                            <th>Oil Change</th>
                            <th>Washing</th>
                            <th>Recovery</th>
                            <th>Credit Sales</th>
                            <th>Commission</th>
                            <th>Expense</th>
                            <th>Balance</th>
                            <th>SaleID</th>
                            <th>ExpenseID</th>
                            <th>PaymentID</th>
                            <th>OvertimeID</th>
                            <th>OvertimeAmount</th>
                            <th>SettlementID</th>
                            <th>Remarks</th>
                            {userRole === 'manager' && <th>Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {dailySales.length > 0 ? (
                            dailySales.map((sale, index) => (
                                sale.Service !== null ? (
                                    <tr key={index}>
                                        <td>{index + 1}</td>
                                        <td>{formatDate(sale.Date)}</td>
                                        <td>{sale.Service}</td>
                                        <td>{sale.VehicleNumber}</td>
                                        <td>{sale.VehicleType}</td>
                                        <td>{sale.Driver}</td>
                                        <td>{sale.CompanyName}</td>
                                        <td>{sale.OilChange ? `AED ${sale.OilChange}` : '-'}</td>
                                        <td>{sale.Washing ? `AED ${sale.Washing}` : '-'}</td>
                                        <td>{sale.Recovery ? `AED ${sale.Recovery}` : '-'}</td>
                                        <td>{sale.CreditSales ? `AED ${sale.CreditSales}` : '-'}</td>
                                        <td>{sale.Commission ? `AED ${sale.Commission}` : '-'}</td>
                                        <td>{sale.Expense ? `AED ${sale.Expense}` : '-'}</td>
                                        <td>{sale.Balance ? `AED ${sale.Balance}` : '-'}</td>
                                        <td>{sale.SaleID || '-'}</td>
                                        <td>{sale.ExpenseID || '-'}</td>
                                        <td>{sale.PaymentID || '-'}</td>
                                        <td>{sale.OvertimeID || '-'}</td>
                                        <td>{sale.OvertimeAmount ? `AED ${sale.OvertimeAmount}` : '-'}</td>
                                        <td>{sale.SettlementID || '-'}</td>
                                        <td>{sale.Remarks}</td>
                                        {userRole === 'manager' && (
                                            <td className="actions-cell">
                                                <button
                                                    className="edit-btn"
                                                    onClick={() => handleEdit(sale)}
                                                    title="Edit"
                                                >
                                                    ✏️
                                                </button>
                                                <button
                                                    className="delete-btn"
                                                    onClick={() => handleDelete(sale)}
                                                    title="Delete"
                                                >
                                                    🗑️
                                                </button>
                                            </td>
                                        )}
                                    </tr>
                                ) : (
                                    // Separate row for expenses
                                    <tr key={`expense-${index}`} className="expense-row">
                                        <td colSpan="7"><strong>Expense</strong></td>
                                        <td>-</td>
                                        <td colSpan="2"><strong>{sale.Expense}</strong></td>
                                        <td>-</td>
                                        {userRole === 'manager' && <td></td>}
                                    </tr>
                                )
                            ))
                        ) : (
                            <tr>
                                <td colSpan={userRole === 'manager' ? 22 : 21}>No data available for the selected date.</td>
                            </tr>
                        )}
                        {/* Add total row */}
                        {dailySales.length > 0 && (
                            <tr className="total-row">
                                <td colSpan="7"><strong>Total</strong></td>
                                <td><strong>{totals.OilChange > 0 ? `AED ${totals.OilChange.toFixed(2)}` : '-'}</strong></td>
                                <td><strong>{totals.Washing > 0 ? `AED ${totals.Washing.toFixed(2)}` : '-'}</strong></td>
                                <td><strong>{totals.Recovery > 0 ? `AED ${totals.Recovery.toFixed(2)}` : '-'}</strong></td>
                                <td><strong>{totals.CreditSales > 0 ? `AED ${totals.CreditSales.toFixed(2)}` : '-'}</strong></td>
                                <td><strong>{totals.Commission !== 0 ? `AED ${Math.abs(totals.Commission).toFixed(2)}` : '-'}</strong></td>
                                <td><strong>{totals.Expense > 0 ? `AED ${totals.Expense.toFixed(2)}` : '-'}</strong></td>
                                <td><strong>{totals.Balance !== 0 ? `AED ${totals.Balance}` : '-'}</strong></td>
                                <td colSpan="7">-</td>
                                {userRole === 'manager' && <td>-</td>}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* ✅ Summary Card Goes Here */}
            <div className="summary-card">
                <h2>Summary</h2>
                <div className="summary-content">
                    <div className="summary-row">
                        <span>Date:</span>
                        <span>{selectedDate ? formatDate(selectedDate) : '-'}</span>
                    </div>
                    <div className="summary-row">
                        <span>Vehicle Washing Sale:</span>
                        <span>{totals.Washing}</span>
                    </div>
                    <div className="summary-row highlighted">
                        <span>Credit Sales:</span>
                        <span>{totals.CreditSales}</span>
                    </div>
                    <div className="summary-row">
                        <span>Oil + Filter Change:</span>
                        <span>{totals.OilChange}</span>
                    </div>
                    <hr />
                    <div className="summary-row total">
                        <span>Total Sale Amount:</span>
                        <span>{(totals.OilChange + totals.Washing)}</span>
                    </div>
                    <div className="summary-row expense">
                        <span>Less Expenditures:</span>
                        <span>{totals.Expense}</span>
                    </div>
                    <div className="summary-row expense">
                        <span>Less Commission:</span>
                        <span>{totals.Commission}</span>
                    </div>
                    <div className="summary-row expense">
                        <span>Recovery:</span>
                        <span>{totals.Recovery}</span>
                    </div>
                    <hr />
                    <div className="summary-row total-final">
                        <span>Balance:</span>
                        <span>{totals.Balance}</span>
                    </div>
                    <div className="summary-row cash-received">
                        <span>Cash Received:</span>
                        <span>{totals.cashReceived}</span>
                    </div>
                </div>
            </div>
            {showEditModal && renderEditForm()}
        </div>
    );

};

export default DailySales;
